/** Steering (people only: holds, project, policies, grants), genesis and sync, and outward actions [P2]. */
import { allowEntryKey, allowListProblems } from "@minga/protocol"
import { managesProject, mayManage } from "../authorize"
import { BRIEF_MAX_CHARS } from "../brief"
import { contentHash } from "../hash"
import { routeCard } from "../multiplayer"
import { documentViolations, PolicyPresets, upgradedDocument } from "../policy"
import { presetUpgrades0134 } from "../preset-upgrade"
import { type ChangeProjectSettings, settingsPatchOf, settingsStaleProblem } from "../project-settings"
import { isTimeZone } from "../recurrence"
import { escalate, openProposal } from "../runs"
import {
  areaOf,
  edgeOf,
  goalOf,
  isOpenItem,
  isOpenProposal,
  isRunEnded,
  itemOf,
  must,
  policyOf,
  proposalOf,
  runOf,
} from "../state"
import type { Command, Hold, Ids, PolicyDocument, Project } from "../types"
import { powersViolation } from "../validate"
import { presetKeyOf } from "../workers"
import { forbidden, invalid, notFound, reject, Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

const requireUser = (tx: Tx): Ids["UserId"] => {
  if (tx.actor._tag !== "user") forbidden("actor_kind", "only people steer")
  return tx.actor.userId
}

const requireProjectManager = (tx: Tx) => {
  const u = requireUser(tx)
  const p = tx.state.project
  if (!p) invalid("the project does not exist yet", "")
  // decision 0129: the project's admins (and workspace owners/admins)
  if (!managesProject(tx.state, u)) forbidden("not_owner", "only the project's admins may do this")
}

export const setHold = (tx: Tx, c: Cmd<"SetHold">): void => {
  const u = requireUser(tx)
  let rec: { readonly hold: Hold; readonly ownerId: Ids["UserId"] } | undefined
  switch (c.ref._tag) {
    case "goal":
      rec = goalOf(tx.state, c.ref.id)
      break
    case "item":
      rec = itemOf(tx.state, c.ref.id)
      break
    case "edge":
      rec = edgeOf(tx.state, c.ref.id)
      break
    default:
      invalid("only goals, items and edges take holds", "ref")
  }
  if (!rec) notFound(c.ref)
  if (!mayManage(tx.state, u, rec.ownerId) && !tx.state.members.some((m) => m.userId === u))
    forbidden("not_owner", "only members set holds")
  if (c.hold._tag !== "none" && c.hold.by !== u) invalid("a hold is set in your own name", "hold.by")
  tx.emit({ _tag: "HoldSet", ref: c.ref, hold: c.hold, before: rec.hold })
}

/**
 * UpdateProject (K5): every check runs against the resulting project. Removing repos prunes `baseBranches` in the same
 * patch; pausing or archiving cancels the project's worker runs that haven't started (K8).
 */
export const updateProject = (tx: Tx, c: Cmd<"UpdateProject">): void => {
  requireProjectManager(tx)
  applyProjectPatch(tx, c)
}

/**
 * Decision 0119: a lead's settings card, applied as the person who answered it. Only the settings keys (never the
 * allowlist, repos, status, policies or members); refused when a value changed since the card opened. The areas' WIP
 * the card carries is applied by the caller (`UpdateArea` as the same person).
 */
export const changeProjectSettings = (tx: Tx, e: ChangeProjectSettings): void => {
  requireProjectManager(tx)
  const stale = settingsStaleProblem(tx.state, e)
  if (stale) invalid(stale, "effects")
  const patch = settingsPatchOf(e.patch)
  if (Object.keys(patch).length > 0) applyProjectPatch(tx, { _tag: "UpdateProject", patch })
}

/** Decision 0119: can this settings patch apply to the project as it is (every UpdateProject check but who)? */
export const checkProjectSettings = (tx: Tx, patch: ChangeProjectSettings["patch"]): void => {
  const scratch = new Tx(tx.state, tx.ctx)
  scratch.scope = { ...tx.scope }
  const only = settingsPatchOf(patch)
  if (Object.keys(only).length > 0) applyProjectPatch(scratch, { _tag: "UpdateProject", patch: only })
}

const applyProjectPatch = (tx: Tx, c: Cmd<"UpdateProject">): void => {
  if (c.patch.description !== undefined)
    invalid(
      "the brief changes only through ReviseBrief (Settings › General, or a plan change)",
      "patch.description",
    )
  const s = tx.state
  const p = must(s.project, "project")
  const patch: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(c.patch)) if (v !== undefined) patch[k] = v
  if (Object.keys(patch).length === 0) invalid("empty patch", "patch")
  if (typeof patch.name === "string") patch.name = patch.name.trim()
  // detaching repos prunes their base branches (decide writes it, so evolve never invents values)
  if (c.patch.repos !== undefined && c.patch.baseBranches === undefined && p.baseBranches !== undefined) {
    const kept = p.baseBranches.filter((b) => c.patch.repos?.includes(b.repoId))
    if (kept.length !== p.baseBranches.length) patch.baseBranches = kept
  }
  const next = { ...p, ...patch } as Project
  if (next.name.length < 1 || next.name.length > 80)
    invalid("a project name has 1–80 characters", "patch.name")
  const wip = next.wip.maxNow
  if (!Number.isInteger(wip) || wip < 1 || wip > 10) invalid("WIP: 1–10 Now items", "patch.wip.maxNow")
  if (new Set(next.repos).size !== next.repos.length) invalid("a repo is attached once", "patch.repos")
  // wave C (decision 0071, F1): the time zone and the daily check's hour (0-23, null = off)
  if (c.patch.cadence !== undefined) {
    if (!isTimeZone(next.cadence.timezone))
      invalid(`${next.cadence.timezone} is not a time zone`, "patch.cadence.timezone")
    const h = next.cadence.dailyCheckHour
    if (h !== undefined && h !== null && (!Number.isInteger(h) || h < 0 || h > 23))
      invalid("the daily check runs at an hour from 0 to 23, or never", "patch.cadence.dailyCheckHour")
  }
  if (next.workers !== undefined) {
    const keys = next.workers.presets.map((x) => x.key)
    if (keys.length < 1 || keys.length > 8) invalid("1–8 worker presets", "patch.workers.presets")
    if (new Set(keys).size !== keys.length) invalid("preset keys are unique", "patch.workers.presets")
    if (keys.filter((k) => k === "default").length !== 1)
      invalid("exactly one preset has the key default", "patch.workers.presets")
    for (const t of s.items) {
      const preset = presetKeyOf(t)
      if (isOpenItem(t) && preset !== null && !keys.includes(preset))
        invalid(`${t.ref} uses the preset ${preset}; change its preset first`, "patch.workers.presets")
    }
  }
  if (next.routing !== undefined) {
    const keys = next.routing.rules.map((r) => r.key)
    if (new Set(keys).size !== keys.length) invalid("routing rule keys are unique", "patch.routing.rules")
  }
  // decision 0096: the project's sandbox allowlist, saved whole
  if (c.patch.allow !== undefined) {
    const problems = allowListProblems(c.patch.allow)
    if (problems.length > 0) invalid(`allowlist: ${problems.join(", ")}`, "patch.allow")
    // a new entry is added in the name of the person saving it (the event log keeps who changed what)
    const known = new Set((p.allow ?? []).map(allowEntryKey))
    const actor = tx.actor
    for (const e of c.patch.allow)
      if (!known.has(allowEntryKey(e)) && (actor._tag !== "user" || e.addedBy !== actor.userId))
        invalid("a new allowlist entry is added in your own name", "patch.allow")
  }
  if (next.baseBranches !== undefined) {
    const ids = next.baseBranches.map((b) => b.repoId)
    if (new Set(ids).size !== ids.length) invalid("one base branch per repo", "patch.baseBranches")
    for (const id of ids)
      if (!next.repos.includes(id)) invalid(`${id} is not attached to this project`, "patch.baseBranches")
  }
  if (c.patch.repos !== undefined) {
    const removed = p.repos.filter((r) => !next.repos.includes(r))
    for (const run of s.runs)
      if (!isRunEnded(run) && (run.placement.requires.repos ?? []).some((r) => removed.includes(r)))
        invalid(`${run.ref} is running in this repo; stop it or wait`, "patch.repos")
  }
  const before: Record<string, unknown> = {}
  for (const k of Object.keys(patch)) before[k] = (p as unknown as Record<string, unknown>)[k] ?? null
  tx.emit({ _tag: "ProjectUpdated", patch: patch as never, before: before as never })
  // K8: pausing or archiving stops new worker attempts; workers that haven't started are cancelled, leased ones finish
  if (p.status === "active" && next.status !== "active")
    for (const run of tx.state.runs)
      if (run.kind === "worker" && (run.state._tag === "requested" || run.state._tag === "waiting"))
        tx.emit({ _tag: "RunCancelRequested", runId: run.id, reason: `project ${next.status}` })
}

const checkDocument = (doc: PolicyDocument) => {
  const v = documentViolations(doc)
  if (v.length > 0) invalid(`${v.join(", ")} stay never for agents`, "document")
}

export const createPolicy = (tx: Tx, c: Cmd<"CreatePolicy">): void => {
  requireProjectManager(tx)
  if (policyOf(tx.state, c.policyId)) invalid("policy id already exists", "policyId")
  checkDocument(c.document)
  if (c.subject._tag === "area" && !areaOf(tx.state, c.subject.areaId))
    notFound({ _tag: "area", id: c.subject.areaId })
  if (
    c.subject._tag === "agent" &&
    !tx.state.agents.some((a) => a.id === (c.subject as { agentId: string }).agentId)
  )
    notFound({ _tag: "agent", id: c.subject.agentId })
  tx.emit({
    _tag: "PolicyCreated",
    policy: {
      ...tx.recordMeta(),
      id: c.policyId,
      subject: c.subject,
      revision: 1,
      document: c.document,
      restoredFrom: null,
    },
  })
}

export const revisePolicy = (tx: Tx, c: Cmd<"RevisePolicy">): void => {
  requireProjectManager(tx)
  const p = policyOf(tx.state, c.policyId) ?? notFound({ _tag: "policy", id: c.policyId })
  if (c.revision !== p.revision)
    reject({
      _tag: "PreconditionFailed",
      ref: { _tag: "policy", id: p.id },
      expected: c.revision as Ids["Seq"],
      actual: p.revision as Ids["Seq"],
    })
  checkDocument(c.document)
  tx.emit({
    _tag: "PolicyRevised",
    policyId: p.id,
    revision: p.revision + 1,
    document: c.document,
    restoredFrom: c.restoredFrom ?? null,
    before: p.document,
  })
}

/**
 * system:upgrade only (decision 0032): for every policy that mentions `like` and not `add`, copy `like`'s rules to
 * `add`, logged as PolicyRevised. Nothing to do → no events. Runs once per project ever (the Project DO's upgrades row).
 */
export const upgradePolicies = (tx: Tx, c: Cmd<"UpgradePolicies">): void => {
  if (tx.actor._tag !== "system" || tx.actor.process !== "upgrade")
    forbidden("actor_kind", "only system:upgrade upgrades policies")
  for (const p of tx.state.policies) {
    const document = upgradedDocument(p.document, c.add, c.like)
    if (!document) continue
    tx.emit({
      _tag: "PolicyRevised",
      policyId: p.id,
      revision: p.revision + 1,
      document,
      restoredFrom: null,
      before: p.document,
    })
  }
}

/**
 * system:upgrade only (decision 0134 addendum): every agent policy that still equals an earlier `lead` or `role`
 * preset exactly gets the 0134 change, logged as PolicyRevised (a policy revision, never work). A customised policy and
 * one already current produce nothing, so a repeat is a no-op. The Project DO issues it once per project.
 */
export const upgradePolicyPresets = (tx: Tx, _c: Cmd<"UpgradePolicyPresets">): void => {
  if (tx.actor._tag !== "system" || tx.actor.process !== "upgrade")
    forbidden("actor_kind", "only system:upgrade upgrades policy presets")
  for (const u of presetUpgrades0134(tx.state)) {
    const p = policyOf(tx.state, u.policyId)
    if (!p) continue
    checkDocument(u.document)
    tx.emit({
      _tag: "PolicyRevised",
      policyId: p.id,
      revision: p.revision + 1,
      document: u.document,
      restoredFrom: null,
      before: p.document,
    })
  }
}

export const grantIntegration = (tx: Tx, c: Cmd<"GrantIntegration">): void => {
  requireProjectManager(tx)
  const area = areaOf(tx.state, c.areaId) ?? notFound({ _tag: "area", id: c.areaId })
  if (area.status !== "active") invalid("area is archived", "areaId")
  const v = powersViolation(area.powers, c.grant)
  if (v) invalid(v, "grant")
  tx.emit({
    _tag: "IntegrationGranted",
    areaId: area.id,
    grant: { ...c.grant, grantedBy: requireUser(tx), grantedAt: tx.now },
  })
}

export const revokeIntegration = (tx: Tx, c: Cmd<"RevokeIntegration">): void => {
  requireProjectManager(tx)
  const area = areaOf(tx.state, c.areaId) ?? notFound({ _tag: "area", id: c.areaId })
  if (!area.grants.some((g) => g.integrationId === c.integrationId))
    invalid("the integration is not granted to this area", "integrationId")
  tx.emit({ _tag: "IntegrationRevoked", areaId: area.id, integrationId: c.integrationId })
}

export const syncWorkspace = (tx: Tx, c: Cmd<"SyncWorkspace">): void => {
  tx.emit({
    _tag: "WorkspaceSynced",
    members: c.members,
    mayor: c.mayor,
    ...(c.changedBy !== undefined ? { changedBy: c.changedBy } : {}),
  })
}

/** Genesis: ProjectCreated + the project policy + the lead's policy + the lead (§5.4). */
export const createProject = (tx: Tx, c: Cmd<"CreateProject">): void => {
  const u = requireUser(tx)
  if (tx.state.project) invalid("the project already exists", "projectId")
  if (c.policyId === c.lead.policyId)
    invalid("the project and the lead need separate policies", "lead.policyId")
  if (c.description.length > BRIEF_MAX_CHARS)
    invalid(`the brief is at most ${BRIEF_MAX_CHARS} characters`, "description")
  const meta = { ...tx.recordMeta(), ownerId: u }
  tx.emit({
    _tag: "ProjectCreated",
    project: {
      ...meta,
      visibility: c.visibility ?? "workspace",
      id: c.projectId,
      workspaceId: c.workspaceId,
      slug: c.slug,
      name: c.name,
      description: c.description,
      status: "active",
      leadAgentId: c.lead.agentId,
      repos: c.repos,
      wip: { maxNow: 3 },
      cadence: { timezone: "UTC", digestCron: null, weeklyReview: null, cycleWeeks: null },
      policyId: c.policyId,
    },
  })
  tx.emit({
    _tag: "PolicyCreated",
    policy: {
      ...tx.recordMeta(),
      id: c.policyId,
      subject: { _tag: "project" },
      revision: 1,
      document: PolicyPresets.project,
      restoredFrom: null,
    },
  })
  tx.emit({
    _tag: "PolicyCreated",
    policy: {
      ...tx.recordMeta(),
      id: c.lead.policyId,
      subject: { _tag: "agent", agentId: c.lead.agentId },
      revision: 1,
      document: PolicyPresets.lead,
      restoredFrom: null,
    },
  })
  tx.emit({
    _tag: "AgentHired",
    agent: {
      ...tx.recordMeta(),
      id: c.lead.agentId,
      role: "lead",
      projectId: c.projectId,
      areaId: null,
      name: c.lead.name,
      handle: c.lead.handle,
      title: "Lead",
      avatar: { emoji: null, color: "slate" },
      charter: { mission: `Lead ${c.name}`, body: "" },
      mind: c.lead.mind,
      hands: { agent: null, model: null, effort: null },
      policyId: c.lead.policyId,
      wake: { debounceMs: 60_000 },
      status: "active",
    },
  })
}

/** [P2] A worker (or the owner agent) on a publish item asks to execute one outward call: always a proposal. */
export const requestOutwardAction = (tx: Tx, c: Cmd<"RequestOutwardAction">): void => {
  const a = tx.actor
  const t = itemOf(tx.state, c.itemId) ?? notFound({ _tag: "item", id: c.itemId })
  if (t.kind !== "publish") invalid("outward actions run only on publish items", "itemId")
  if (a._tag === "worker" && a.itemId !== t.id)
    forbidden("not_owner", "a worker requests outward actions for its own item")
  if (a._tag === "agent" && !(t.owner?._tag === "agent" && t.owner.agentId === a.agentId))
    forbidden("not_owner", "only the item's owner requests outward actions")
  const area = t.areaId ? areaOf(tx.state, t.areaId) : undefined
  const grant = area?.grants.find((g) => g.integrationId === c.integrationId)
  if (!grant?.outward.includes(c.tool))
    invalid(`${c.tool} is not an outward tool granted to this area`, "tool")
  if (tx.state.proposals.some((p) => p.id === c.proposalId))
    invalid("proposal id already exists", "proposalId")
  const argsHash = contentHash(c.args)
  const intentKey = `outward:${t.id}:${c.integrationId}:${c.tool}:${argsHash}`
  const dup = tx.state.proposals.find((p) => p.intentKey === intentKey && isOpenProposal(p))
  if (dup)
    reject({
      _tag: "Duplicate",
      existing: { _tag: "proposal", id: dup.id },
      reason: `${dup.ref} already holds this call`,
    })
  // decision 0127: for the person responsible for the item, else its asker, else the project's admins
  const owner = routeCard(tx.state, a, { items: [t.id] }) ?? tx.owner()
  openProposal(tx, {
    id: c.proposalId,
    ownerId: owner,
    body: {
      _tag: "outward_action",
      itemId: t.id,
      integrationId: c.integrationId,
      tool: c.tool,
      args: c.args,
      argsHash,
      // The pinned description+schema hash lives in the workspace integration registry; until the command carries
      // it, the proposal binds the (integration, tool) identity and the gateway re-checks the pinned hash.
      toolSchemaHash: contentHash({ integrationId: c.integrationId, tool: c.tool }),
      preview: c.preview,
      executor: null,
    },
    proposedBy: a,
    reasoning: c.preview.summary,
    oneLine: c.preview.summary,
    risk: "high",
    reversibility: "irreversible",
    tier: "blocking",
    blocks: [{ _tag: "item", id: t.id }],
    tainted: true, // outward approvals are always a person's
    approver: { _tag: "human", userId: owner },
    decideBy: null,
    expiresAt: null, // rev 2.1 (decision 0018): proposals do not expire
    intentKey,
  })
}

/** [P2] The gateway or the publish run reports the one execution of an approved call. */
export const recordOutwardResult = (tx: Tx, c: Cmd<"RecordOutwardResult">): void => {
  const p = proposalOf(tx.state, c.proposalId) ?? notFound({ _tag: "proposal", id: c.proposalId })
  if (p.body._tag !== "outward_action") invalid("not an outward action", "proposalId")
  if (p.state !== "approved" || p.revision !== c.revision)
    reject({ _tag: "StaleProposal", proposalId: p.id, currentRevision: p.revision, state: p.state })
  const run = runOf(tx.state, c.runId) ?? notFound({ _tag: "run", id: c.runId })
  if (run.kind !== "publish" || run.publish?.proposalId !== p.id || run.publish.revision !== p.revision)
    invalid("the run is not this proposal's publish run", "runId")
  const t = itemOf(tx.state, p.body.itemId)
  if (c.result._tag === "ok") {
    tx.emit({
      _tag: "OutwardActionExecuted",
      proposalId: p.id,
      revision: p.revision,
      runId: run.id,
      resultRef: c.result.resultRef,
    })
    tx.emit({ _tag: "ProposalApplied", proposalId: p.id, revision: p.revision, changesetId: null })
    if (t && t.status !== "done" && t.status !== "cancelled")
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: t.status,
        to: "done",
        cause: { _tag: "completed", evidence: [{ _tag: "run", runId: run.id }] },
        blocked: null,
        resolution: null,
      })
  } else {
    tx.emit({
      _tag: "OutwardActionFailed",
      proposalId: p.id,
      revision: p.revision,
      runId: run.id,
      error: c.result.error,
    })
    tx.emit({
      _tag: "ProposalApplyFailed",
      proposalId: p.id,
      revision: p.revision,
      rejection: {
        _tag: "Invalid",
        issues: [{ path: "outward", message: c.result.error, changeIndex: null }],
      },
    })
    if (t && (t.status === "in_review" || t.status === "todo"))
      escalate(tx, t, "non_retryable", `the outward action failed: ${c.result.error}`, run.id)
  }
}
